import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TrihybridCrossCalculator = () => {
  const [formData, setFormData] = useState({
    trait1Parent1: '',
    trait1Parent2: '',
    trait2Parent1: '',
    trait2Parent2: '',
    trait3Parent1: '',
    trait3Parent2: '',
    trait1Label: 'Trait 1',
    trait2Label: 'Trait 2',
    trait3Label: 'Trait 3'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const getPhenotype = (genotype) => {
    if (!genotype) return '';
    const alleles = genotype.split('');
    const dominant = alleles.some(a => a === a.toUpperCase());
    return dominant ? alleles[0].toUpperCase() : alleles[1].toLowerCase();
  };

  const generatePunnettSquare = () => {
    const {
      trait1Parent1, trait1Parent2,
      trait2Parent1, trait2Parent2,
      trait3Parent1, trait3Parent2
    } = formData;

    const parent1 = trait1Parent1 + trait2Parent1 + trait3Parent1;
    const parent2 = trait1Parent2 + trait2Parent2 + trait3Parent2;

    const gametes1 = generateGametes(parent1);
    const gametes2 = generateGametes(parent2);

    const square = [];
    for (let i = 0; i < gametes1.length; i++) {
      const row = [];
      for (let j = 0; j < gametes2.length; j++) {
        const genotype = sortGenotype(gametes1[i] + gametes2[j]);
        const phenotype = getPhenotype(genotype);
        row.push({ genotype, phenotype });
      }
      square.push(row);
    }

    return square;
  };

  const generateGametes = (parent) => {
    const gametes = [];
    const alleles = parent.split('');
    const combinations = generateCombinations(alleles);
    combinations.forEach(comb => gametes.push(comb.join('')));
    return gametes;
  };

  const generateCombinations = (alleles) => {
    if (alleles.length === 0) return [[]];
    if (alleles.length === 1) return [[alleles[0]]];

    const first = alleles[0];
    const rest = alleles.slice(1);
    const combinations = generateCombinations(rest);

    const result = [];
    combinations.forEach(comb => {
      result.push([first, ...comb]);
      result.push([...comb, first]);
      for (let i = 1; i < comb.length; i++) {
        const start = comb.slice(0, i);
        const end = comb.slice(i);
        result.push([...start, first, ...end]);
      }
    });

    const uniqueCombinations = Array.from(new Set(result.map(comb => comb.join(''))))
      .map(comb => comb.split(''));

    return uniqueCombinations;
  };

  const sortGenotype = (genotype) => {
    const alleles = genotype.split('');
    alleles.sort();
    return alleles.join('');
  };

  const calculateResults = () => {
    const {
      trait1Parent1, trait1Parent2,
      trait2Parent1, trait2Parent2,
      trait3Parent1, trait3Parent2
    } = formData;

    if (!trait1Parent1 || !trait1Parent2 ||
        !trait2Parent1 || !trait2Parent2 ||
        !trait3Parent1 || !trait3Parent2) {
      setError('Please enter genotypes for all traits');
      return;
    }

    const inputGenotypes = [trait1Parent1, trait1Parent2,
                          trait2Parent1, trait2Parent2,
                          trait3Parent1, trait3Parent2];
    
    const isValidGenotype = (genotype) => {
      const alleles = genotype.split('');
      if (alleles.length !== 2) return false;
      const [a1, a2] = alleles;
      return (a1 === a1.toUpperCase() && a2 === a2.toLowerCase()) ||
             (a1 === a1.toLowerCase() && a2 === a2.toUpperCase()) ||
             (a1 === a1.toUpperCase() && a2 === a2.toUpperCase()) ||
             (a1 === a1.toLowerCase() && a2 === a2.toLowerCase());
    };

    if (inputGenotypes.some(genotype => !isValidGenotype(genotype))) {
      setError('Please enter valid genotypes (e.g., AA, Aa, aa)');
      return;
    }

    const square = generatePunnettSquare();
    const genotypeCounts = {};
    const phenotypeCounts = {};

    square.forEach(row => {
      row.forEach(cell => {
        genotypeCounts[cell.genotype] = (genotypeCounts[cell.genotype] || 0) + 1;
        phenotypeCounts[cell.phenotype] = (phenotypeCounts[cell.phenotype] || 0) + 1;
      });
    });

    const total = square.length * square[0].length;
    const genotypeRatios = Object.entries(genotypeCounts).map(([genotype, count]) => ({
      genotype,
      count,
      ratio: `${count}/${total}`
    }));

    const phenotypeRatios = Object.entries(phenotypeCounts).map(([phenotype, count]) => ({
      phenotype,
      count,
      ratio: `${count}/${total}`
    }));

    const chartData = {
      labels: genotypeRatios.map(r => r.genotype),
      datasets: [{
        label: 'Genotype Frequency',
        data: genotypeRatios.map(r => r.count),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };

    setResults({
      square,
      genotypeRatios,
      phenotypeRatios
    });
    setChartData(chartData);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Trihybrid Cross Calculator | Genetic Cross Analysis Tool</title>
        <meta name="description" content="Calculate genetic outcomes for trihybrid crosses with our advanced calculator. Analyze genotype and phenotype ratios for three traits simultaneously." />
        <meta name="keywords" content="trihybrid cross calculator, genetic cross analysis, genetics tool, Punnett square calculator, three-trait cross" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">🧬</div>
            <h1 className="text-3xl font-bold text-gray-800">Trihybrid Cross Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate genetic outcomes for a trihybrid cross using Punnett squares. Enter genotypes for three traits in both parents to see all possible offspring combinations.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Trait 1</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent 1:</label>
                    <input
                      type="text"
                      name="trait1Parent1"
                      value={formData.trait1Parent1}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter genotype (e.g., AA, Aa, aa)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent 2:</label>
                    <input
                      type="text"
                      name="trait1Parent2"
                      value={formData.trait1Parent2}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter genotype (e.g., AA, Aa, aa)"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Trait 2</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent 1:</label>
                    <input
                      type="text"
                      name="trait2Parent1"
                      value={formData.trait2Parent1}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter genotype (e.g., BB, Bb, bb)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent 2:</label>
                    <input
                      type="text"
                      name="trait2Parent2"
                      value={formData.trait2Parent2}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter genotype (e.g., BB, Bb, bb)"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Trait 3</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent 1:</label>
                    <input
                      type="text"
                      name="trait3Parent1"
                      value={formData.trait3Parent1}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter genotype (e.g., CC, Cc, cc)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent 2:</label>
                    <input
                      type="text"
                      name="trait3Parent2"
                      value={formData.trait3Parent2}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter genotype (e.g., CC, Cc, cc)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={calculateResults}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Results
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Genetic Outcomes</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Genotype Ratios</h3>
                    <ul className="space-y-2">
                      {results.genotypeRatios.map((ratio, index) => (
                        <li key={index} className="flex items-center">
                          <span className="font-medium text-gray-600 mr-2">
                            {ratio.genotype}:
                          </span>
                          {ratio.ratio}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Phenotype Ratios</h3>
                    <ul className="space-y-2">
                      {results.phenotypeRatios.map((ratio, index) => (
                        <li key={index} className="flex items-center">
                          <span className="font-medium text-gray-600 mr-2">
                            {ratio.phenotype}:
                          </span>
                          {ratio.ratio}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Frequency'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Genotypes'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Genotype Distribution',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Trihybrid Cross Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the Trihybrid Cross Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Parent Genotypes</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter genotypes for each trait (e.g., AA, Aa, aa)</li>
                  <li>Ensure each trait uses different letters (e.g., A for trait 1, B for trait 2)</li>
                  <li>Use uppercase for dominant alleles and lowercase for recessive alleles</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click the "Calculate Results" button</li>
                  <li>View genotype and phenotype ratios</li>
                  <li>Review the distribution chart</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Check genotype ratios for each combination</li>
                  <li>Review phenotype ratios for observable traits</li>
                  <li>Use the chart to visualize distribution</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Notes</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Each trait must use different letters (e.g., A, B, C)</li>
                  <li>Ensure valid genotypes (AA, Aa, aa) for each trait</li>
                  <li>Results show all possible offspring combinations</li>
                  <li>Phenotype ratios show observable traits</li>
                  <li>Genotype ratios show genetic combinations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrihybridCrossCalculator;